import { documentReady } from '@the-shared/document-ready';

type Callback< T extends HTMLElement > = ( element: T ) => void;
type MapCallbacks< T extends HTMLElement > = Map< Symbol, Callback< T > >;
type ElementCallbacks< T extends HTMLElement > = Map<
	string,
	MapCallbacks< T >
>;
type WeakSetElements = WeakSet< HTMLElement >;

const liveElementCallbacks: ElementCallbacks< HTMLElement > = new Map();
const processedElements = new Map< Symbol, WeakSetElements >();

const runCallback = < T extends HTMLElement >(
	element: T,
	callback: Callback< T >,
	key: Symbol
) => {
	if ( ! processedElements.has( key ) ) {
		processedElements.set( key, new WeakSet() );
	}

	const elements = processedElements.get( key ) as WeakSetElements;

	if ( ! elements.has( element ) ) {
		requestAnimationFrame( () => callback( element ) );
		elements.add( element );
	}
};

const observer = new MutationObserver( ( mutations ) => {
	mutations.forEach( ( mutation ) => {
		if ( mutation.type === 'childList' ) {
			mutation.addedNodes.forEach( ( node ) => {
				if ( node instanceof HTMLElement ) {
					liveElementCallbacks.forEach( ( callbacks, selector ) => {
						if ( node.matches( selector ) ) {
							callbacks.forEach( ( callback, key ) => {
								runCallback( node, callback, key );
							} );
						}

						node.querySelectorAll< HTMLElement >(
							selector
						).forEach( ( element ) => {
							callbacks.forEach( ( callback, key ) => {
								runCallback( element, callback, key );
							} );
						} );
					} );
				}
			} );
		}
	} );
} );

documentReady( () => {
	observer.observe( document.body, { childList: true, subtree: true } );
} );

function liveElement< T extends HTMLElement >(
	selector: string,
	callback: Callback< T >
): () => void {
	if ( ! liveElementCallbacks.has( selector ) ) {
		liveElementCallbacks.set( selector, new Map() );
	}

	const key = Symbol();
	const callbacks = liveElementCallbacks.get( selector ) as MapCallbacks< T >;

	callbacks.set( key, callback );

	documentReady( () => {
		document.querySelectorAll< T >( selector ).forEach( ( element ) => {
			runCallback( element, callback, key );
		} );
	} );

	return () => {
		callbacks.delete( key );
		if ( 0 === callbacks.size ) {
			liveElementCallbacks.delete( selector );
		}
	};
}

export { liveElement };
